/*
 * Copyright Mimic Networks, Inc. 2026.
 */

import { TableProps } from 'antd';

import { Node, Tag } from '@/client';
import { QueryWrapper } from '@/components/QueryWrapper';
import { usePaginatedNodesQuery } from '@/hooks/query/usePaginatedNodesQuery';
import { usePaginatedTagsQuery } from '@/hooks/query/usePaginatedTagsQuery';
import { NodesColumns } from '@/pages/Nodes/getNodesColumns';
import { NodesTable } from '@/pages/Nodes/NodesTable';
import { Card } from '@/primitives/Card';

const VISIBLE_COLUMNS: NodesColumns[] = [
  'hostname',
  'tags',
  'connectivityState',
  'operatingSystem',
  'lastSeen',
  'appliedConfiguration',
];

export type SelectNodesStepProps = {
  tenantId: string;
  nodesQuery: ReturnType<typeof usePaginatedNodesQuery>;
  tagsQuery: ReturnType<typeof usePaginatedTagsQuery>;
  selectedNodeIds: string[];
  onSelectionChange: (nodeIds: string[], nodes: Node[]) => void;
  getNodeConfigurationPath: (nodeConfigId: string) => string;
};

export function SelectNodesStep({
  tenantId,
  nodesQuery,
  tagsQuery,
  selectedNodeIds,
  onSelectionChange,
  getNodeConfigurationPath,
}: SelectNodesStepProps) {
  const rowSelection: TableProps<Node>['rowSelection'] = {
    type: 'checkbox',
    preserveSelectedRowKeys: true,
    selectedRowKeys: selectedNodeIds,
    onChange: (_selectedKeys: React.Key[], selectedRows: Node[]) => {
      onSelectionChange(
        selectedRows.map((node) => node.id),
        selectedRows,
      );
    },
    getCheckboxProps: (node: Node) => ({
      name: node.name,
    }),
  };

  return (
    <Card dataTestId="fleet-wizard-select-nodes" padding="sm" bordered style={{ width: '100%' }}>
      <QueryWrapper queryResult={nodesQuery}>
        {(nodesData) => (
          <QueryWrapper queryResult={tagsQuery}>
            {(tagsData: { data: Tag[] }) => (
              <NodesTable
                tenantID={tenantId}
                nodesData={nodesData}
                onParamsChange={nodesQuery.updateQueryParams}
                columns={VISIBLE_COLUMNS}
                tags={tagsData.data}
                getNodeConfigurationPath={getNodeConfigurationPath}
                rowSelection={rowSelection}
                selectedNodeIds={selectedNodeIds}
                hideFilters
              />
            )}
          </QueryWrapper>
        )}
      </QueryWrapper>
    </Card>
  );
}
